import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname.startsWith('/admindea26') && window.location.pathname !== '/admin') {
        window.location.href = '/admindea26';
      }
    }
    return Promise.reject(error);
  }
);

// Order APIs
export const lookupOrder = async (orderNumber, email, phone) => {
  const response = await api.post('/orders/lookup', { 
    order_number: orderNumber,
    email: email,
    phone: phone
  });
  return response.data;
};

// Product APIs
export const getProducts = async ({ productType, collection } = {}) => {
  const params = {};
  if (productType) params.product_type = productType;
  if (collection) params.collection = collection;
  const response = await api.get('/products', { params });
  return response.data;
};

export const getProductDetail = async (productId) => {
  const response = await api.get(`/products/${productId}`);
  return response.data;
};

export const getProductInventory = async (productId) => {
  const response = await api.get(`/products/${productId}/inventory`);
  return response.data;
};

// Return Request APIs
export const createReturnRequest = async (data) => {
  const response = await api.post('/returns', data);
  return response.data;
};

export const getReturnRequest = async (requestId) => {
  const response = await api.get(`/returns/${requestId}`);
  return response.data;
};

export const getReturnsByOrder = async (orderNumber) => {
  const response = await api.get(`/returns/order/${orderNumber}`);
  return response.data;
};

// Get items already requested for an order
export const getRequestedItems = async (orderNumber) => {
  const response = await api.get(`/orders/${orderNumber}/requested-items`);
  return response.data;
};

// Admin APIs
export const adminLogin = async (email, password) => {
  const response = await api.post('/admin/login', { email, password });
  return response.data;
};

export const getAdminReturns = async (status = null, requestType = null) => {
  const params = {};
  if (status) params.status = status;
  if (requestType) params.request_type = requestType;
  const response = await api.get('/admin/returns', { params });
  return response.data;
};

export const updateReturnStatus = async (requestId, data) => {
  const response = await api.put(`/admin/returns/${requestId}`, data);
  return response.data;
};

export const getAdminStats = async () => {
  const response = await api.get('/admin/stats');
  return response.data;
};

// RMA APIs
export const getRMAOffers = async (items, originalOrderTotal = null) => {
  const url = originalOrderTotal ? `/rma/offers?original_order_total=${originalOrderTotal}` : '/rma/offers';
  const response = await api.post(url, items);
  return response.data;
};

export const createRMA = async (data) => {
  const response = await api.post('/rma', data);
  return response.data;
};

export const getRMA = async (rmaId) => {
  const response = await api.get(`/rma/${rmaId}`);
  return response.data;
};

export const getRMAsByOrder = async (orderNumber) => {
  const response = await api.get(`/rma/order/${orderNumber}`);
  return response.data;
};

export const getAdminRMAs = async (status = null) => {
  const params = {};
  if (status) params.status = status;
  const response = await api.get('/admin/rma', { params });
  return response.data;
};

export const updateRMAStatus = async (rmaId, data) => {
  const response = await api.put(`/admin/rma/${rmaId}`, null, { params: data });
  return response.data;
};

// Check payment status for exchange requests
export const checkPaymentStatus = async (requestId) => {
  const response = await api.get(`/admin/returns/${requestId}/payment-status`);
  return response.data;
};

export const checkFulfillmentStatus = async (requestId) => {
  const response = await api.post(`/admin/returns/${requestId}/check-fulfillment`);
  return response.data;
};

// Delete requests (admin only)
export const deleteReturnRequest = async (requestId) => {
  const response = await api.delete(`/admin/returns/${requestId}`);
  return response.data;
};

export const deleteRMA = async (rmaId) => {
  const response = await api.delete(`/admin/rma/${rmaId}`);
  return response.data;
};

// App Settings (Pannello di Controllo)
export const getAppSettings = async () => {
  const response = await api.get('/admin/settings');
  return response.data;
};

export const getPublicSettings = async () => {
  const response = await api.get('/public/settings');
  return response.data;
};

export const updateAppSettings = async (data) => {
  const response = await api.put('/admin/settings', data);
  return response.data;
};

// Get current user info
export const getCurrentUser = async () => {
  const response = await api.get('/admin/me');
  return response.data;
};

// Manual status update with email suppression
export const manualStatusUpdateReturn = async (requestId, data) => {
  const response = await api.put(`/admin/returns/${requestId}/manual-status`, data);
  return response.data;
};

export const manualStatusUpdateRMA = async (rmaId, data) => {
  const response = await api.put(`/admin/rma/${rmaId}/manual-status`, data);
  return response.data;
};

// Approve/reject out-of-stock exchange
export const approveRejectExchange = async (requestId, data) => {
  const response = await api.put(`/admin/returns/${requestId}/approve-exchange`, data);
  return response.data;
};

// Workflow actions
export const returnWorkflowAction = async (requestId, data) => {
  const response = await api.put(`/admin/returns/${requestId}/workflow`, data);
  return response.data;
};

export const rmaWorkflowAction = async (rmaId, data) => {
  const response = await api.put(`/admin/rma/${rmaId}/workflow`, data);
  return response.data;
};

// Waybill (Lettera di Vettura)
export const uploadWaybill = async (requestId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/admin/returns/${requestId}/upload-waybill`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const sendWaybillEmail = async (requestId) => {
  const response = await api.post(`/admin/returns/${requestId}/send-waybill-email`);
  return response.data;
};

export const markAsPaid = async (requestId) => {
  const response = await api.post(`/admin/returns/${requestId}/mark-paid`);
  return response.data;
};

export const notifyStorePickup = async (requestId, data = {}) => {
  const response = await api.put(`/admin/returns/${requestId}/notify-store-pickup`, data);
  return response.data;
};

export const downloadWaybill = async (requestId) => {
  const response = await api.get(`/admin/waybill/${requestId}`, {
    responseType: 'blob'
  });
  return response;
};

// Email Templates
export const getEmailTemplates = async () => {
  const response = await api.get('/admin/email-templates');
  return response.data;
};

export const updateEmailTemplate = async (templateKey, data) => {
  const response = await api.put(`/admin/email-templates/${templateKey}`, data);
  return response.data;
};

export const resetEmailTemplate = async (templateKey) => {
  const response = await api.delete(`/admin/email-templates/${templateKey}`);
  return response.data;
};

export const previewEmailTemplate = async (templateKey, data) => {
  const response = await api.post(`/admin/email-templates/${templateKey}/preview`, data);
  return response.data;
};

// Test Mode
export const purgeTestData = async () => {
  const response = await api.delete('/admin/purge-test-data');
  return response.data;
};

export const getTestDataCount = async () => {
  const response = await api.get('/admin/test-data-count');
  return response.data;
};


// Email Log
export const getEmailLog = async (requestId) => {
  const response = await api.get(`/admin/returns/${requestId}/email-log`);
  return response.data;
};

export const resendEmail = async (requestId, emailIndex, toOverride = null) => {
  const body = { email_index: emailIndex };
  if (toOverride) body.to_override = toOverride;
  const response = await api.post(`/admin/returns/${requestId}/resend-email`, body);
  return response.data;
};

// Receipt PDF
export const downloadReceipt = async (requestId) => {
  const response = await api.get(`/admin/returns/${requestId}/receipt`, { responseType: 'blob' });
  return response;
};

// Test Simulator
export const createTestOrder = async (data) => {
  const response = await api.post('/admin/test/create-order', data);
  return response.data;
};

export const getTestOrders = async () => {
  const response = await api.get('/admin/test/orders');
  return response.data;
};

export const deleteTestOrder = async (orderNumber) => {
  const response = await api.delete(`/admin/test/orders/${orderNumber}`);
  return response.data;
};

export const bulkStatusUpdate = async (requestIds, status, sendEmails = false) => {
  const response = await api.post('/admin/returns/bulk-status', {
    request_ids: requestIds,
    status,
    send_emails: sendEmails
  });
  return response.data;
};

export const getPendingEmail = async (requestId) => {
  const response = await api.get(`/admin/returns/${requestId}/pending-email`);
  return response.data;
};

export const sendPendingEmail = async (requestId) => {
  const response = await api.post(`/admin/returns/${requestId}/send-pending-email`);
  return response.data;
};





export default api;
